import { useState, useRef, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { Clipboard } from '@capacitor/clipboard';
import { useAuthStore } from '../stores/authStore';
import { trackPlatformClick } from './AdminDashboard';
import { API_BASE_URL } from '../config/api';

declare global {
  interface Window {
    google: any;
  }
}

interface PlaceResult {
  address: string;
  lat: number;
  lng: number;
  placeId: string;
}

interface PlatformEstimate {
  platform: string;
  id: string;
  price: number;
  estimatedTime: number;
  vehicleType: string;
  logo: string;
  deepLink: string;
  storeLink: string;
  surgeMultiplier: number;
  platformConfig: PlatformConfig;
  // Rango de precio estimado (para plataformas sin tarifa oficial)
  priceMin: number;
  priceMax: number;
  confidence: ConfidenceMeta;
}

interface HistoryEntry {
  id: string;
  date: string;
  origin: PlaceResult;
  destination: PlaceResult;
  originText: string;
  destText: string;
  cheapest: string;
  cheapestPrice: number;
}

interface PlatformConfig {
  id: string;
  name: string;
  logo: string;
  colorClass: string;
  androidPackage: string;
  androidStore: string;
  iosStore: string;
  iosAppId: string;
  // URI scheme nativo de la app
  uriScheme: string;
  // Construye el deep link (Universal Link para Uber, URI scheme para otros)
  buildDeepLink: (o: PlaceResult, d: PlaceResult) => string;
  // Indica si usa Universal Links (como Uber) - no necesita lógica especial
  usesUniversalLink: boolean;
  // true para apps que NO reciben el destino por deep link (DiDi, Cabify, InDriver,
  // Taxis Libres). En esos casos copiamos la dirección de destino al portapapeles
  // para que el usuario solo tenga que pegarla en la app.
  copiesAddress?: boolean;
}

const PLATFORMS: PlatformConfig[] = [
  {
    id: 'uber',
    name: 'Uber',
    logo: '🚗',
    colorClass: 'bg-black',
    androidPackage: 'com.ubercab',
    androidStore: 'https://play.google.com/store/apps/details?id=com.ubercab',
    iosStore: 'https://apps.apple.com/app/uber/id368677368',
    iosAppId: '368677368',
    uriScheme: 'uber://',
    usesUniversalLink: true,
    buildDeepLink: (o: PlaceResult, d: PlaceResult) =>
      `https://m.uber.com/ul/?action=setPickup&pickup[latitude]=${o.lat}&pickup[longitude]=${o.lng}&pickup[formatted_address]=${encodeURIComponent(o.address)}&dropoff[latitude]=${d.lat}&dropoff[longitude]=${d.lng}&dropoff[formatted_address]=${encodeURIComponent(d.address)}`,
  },
  {
    id: 'didi',
    name: 'DiDi',
    logo: '🟠',
    colorClass: 'bg-orange-500',
    androidPackage: 'com.didiglobal.passenger',
    androidStore: 'https://play.google.com/store/apps/details?id=com.didiglobal.passenger',
    iosStore: 'https://apps.apple.com/app/didi-rider/id1362398401',
    iosAppId: '1362398401',
    // DiDi: abrir por esquema nativo. El Universal Link https://oia.didiglobal.com
    // abría el NAVEGADOR en Android en vez de la app. Usamos el esquema "diditaxi://"
    // para abrir la app directamente. Pendiente de verificar el host exacto en el
    // dispositivo (ver nota en openNativeApp: para DiDi se usa el launch del paquete).
    uriScheme: 'diditaxi://',
    usesUniversalLink: false,
    copiesAddress: true,
    buildDeepLink: (_o: PlaceResult, _d: PlaceResult) => `diditaxi://`,
  },
  {
    id: 'indriver',
    name: 'InDriver',
    logo: '🟢',
    colorClass: 'bg-green-600',
    androidPackage: 'sinet.startup.inDriver',
    androidStore: 'https://play.google.com/store/apps/details?id=sinet.startup.inDriver',
    iosStore: 'https://apps.apple.com/app/indriver/id1050809498',
    iosAppId: '1050809498',
    // InDriver abre con el host "open" (verificado en dispositivo: indrive://open).
    uriScheme: 'indrive://open',
    usesUniversalLink: false,
    copiesAddress: true,
    buildDeepLink: (_o: PlaceResult, _d: PlaceResult) => `indrive://open`,
  },
  {
    id: 'cabify',
    name: 'Cabify',
    logo: '🟣',
    colorClass: 'bg-purple-600',
    androidPackage: 'com.cabify.rider',
    androidStore: 'https://play.google.com/store/apps/details?id=com.cabify.rider',
    iosStore: 'https://apps.apple.com/app/cabify/id476087442',
    iosAppId: '476087442',
    uriScheme: 'cabify://',
    usesUniversalLink: false,
    copiesAddress: true,
    buildDeepLink: (_o: PlaceResult, _d: PlaceResult) => `cabify://`,
  },
  {
    id: 'yango',
    name: 'Yango',
    logo: '🔴',
    colorClass: 'bg-red-500',
    androidPackage: 'com.yandex.yango',
    androidStore: 'https://play.google.com/store/apps/details?id=com.yandex.yango',
    iosStore: 'https://apps.apple.com/app/yango-taxi-food-delivery/id1437157286',
    iosAppId: '1437157286',
    // Esquema real de la app Yango en Colombia (verificado en el dispositivo:
    // com.yandex.yango declara el scheme "yandexyango", no "yandextaxi").
    uriScheme: 'yandexyango://',
    usesUniversalLink: false,
    buildDeepLink: (o: PlaceResult, d: PlaceResult) =>
      `yandexyango://route?start-lat=${o.lat}&start-lon=${o.lng}&end-lat=${d.lat}&end-lon=${d.lng}`,
  },
  {
    id: 'taxislibres',
    name: 'Taxis Libres',
    logo: '🚕',
    colorClass: 'bg-yellow-500',
    androidPackage: 'com.cotech.taxislibres',
    androidStore: 'https://play.google.com/store/apps/details?id=com.cotech.taxislibres',
    iosStore: 'https://apps.apple.com/co/app/taxis-libres-pasajeros/id686270238',
    iosAppId: '686270238',
    // Taxi regulado por taxímetro oficial. No tiene deep link público con coordenadas;
    // abrimos la app en su pantalla principal. El esquema se valida en dispositivo; si no
    // está instalada, AppLauncher cae a la tienda.
    uriScheme: 'taxislibres://',
    usesUniversalLink: false,
    copiesAddress: true,
    buildDeepLink: (_o: PlaceResult, _d: PlaceResult) => `taxislibres://`,
  },
];

// Nivel de confianza de la estimación por plataforma.
// - oficial:    tarifa publicada y verificable (mostramos precio puntual)
// - estimado:   sin fuente oficial pública, calculado a partir de estimaciones (mostramos rango)
// - negociable: la plataforma no fija precio, el usuario negocia (mostramos "desde ~$X, negociable")
type ConfidenceLevel = 'oficial' | 'estimado' | 'negociable';

interface ConfidenceMeta {
  level: ConfidenceLevel;
  label: string;
  badgeClass: string;
  tooltip: string;
  // Semi-ancho del rango como fracción del precio (ej. 0.08 => ±8%). 0 = precio puntual.
  rangeSpread: number;
}

const CONFIDENCE: Record<string, ConfidenceMeta> = {
  cabify: {
    level: 'oficial',
    label: 'Oficial',
    badgeClass: 'bg-green-100 text-green-700',
    tooltip: 'Tarifa publicada y verificable en el sitio oficial de Cabify.',
    rangeSpread: 0,
  },
  uber: {
    level: 'estimado',
    label: 'Estimado',
    badgeClass: 'bg-amber-100 text-amber-700',
    tooltip: 'Estimación referencial. Uber no publica sus tarifas; el precio real puede variar por demanda y tráfico.',
    rangeSpread: 0.08,
  },
  didi: {
    level: 'estimado',
    label: 'Estimado',
    badgeClass: 'bg-amber-100 text-amber-700',
    tooltip: 'Estimación referencial. DiDi no publica sus tarifas; el precio real puede variar por demanda y tráfico.',
    rangeSpread: 0.08,
  },
  yango: {
    level: 'estimado',
    label: 'Estimado',
    badgeClass: 'bg-amber-100 text-amber-700',
    tooltip: 'Estimación referencial. Yango no publica sus tarifas; el precio real puede variar por demanda y tráfico.',
    rangeSpread: 0.08,
  },
  indriver: {
    level: 'negociable',
    label: 'Negociable',
    badgeClass: 'bg-blue-100 text-blue-700',
    tooltip: 'En InDriver tú propones el precio y el conductor acepta o contraoferta. Este es un punto de partida sugerido.',
    rangeSpread: 0.15,
  },
  taxislibres: {
    level: 'oficial',
    label: 'Tarifa taxímetro',
    badgeClass: 'bg-green-100 text-green-700',
    tooltip: 'Taxi con taxímetro regulado por la tarifa oficial del distrito. El valor mostrado es una estimación según distancia y tiempo.',
    rangeSpread: 0.10,
  },
};

const DEFAULT_CONFIDENCE: ConfidenceMeta = {
  level: 'estimado',
  label: 'Estimado',
  badgeClass: 'bg-amber-100 text-amber-700',
  tooltip: 'Estimación referencial. El precio real puede variar.',
  rangeSpread: 0.08,
};

// Redondea a la centena más cercana (formato de precios COP)
function roundTo100(value: number): number {
  return Math.round(value / 100) * 100;
}

// Detect mobile OS
function getMobileOS(): 'android' | 'ios' | 'unknown' {
  const ua = navigator.userAgent.toLowerCase();
  if (/android/.test(ua)) return 'android';
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  return 'unknown';
}

// Abre la app de transporte de forma robusta en las 3 plataformas (Android, iOS, web).
//
// - Dentro de la app instalada (Capacitor nativo): usa el plugin AppLauncher, que
//   comprueba si la app está instalada (canOpenUrl) y la abre (openUrl). Si no está
//   instalada, abre la tienda correspondiente. Funciona igual en Android e iOS.
//   IMPORTANTE (Android 11+): los esquemas/paquetes deben estar declarados en
//   AndroidManifest.xml dentro de <queries>, si no canOpenUrl siempre da false.
// - En navegador web: se mantiene el comportamiento por redirección que ya funciona.
async function openNativeApp(platform: PlatformConfig, deepLink: string) {
  const store = getMobileOS() === 'ios' ? platform.iosStore : platform.androidStore;

  // --- App nativa (Capacitor) ---
  if (Capacitor.isNativePlatform()) {
    const isAndroid = Capacitor.getPlatform() === 'android';
    try {
      // Para saber si la app está instalada usamos SIEMPRE su uriScheme nativo
      // (resuelve solo si la app existe). No usamos el Universal Link para esto,
      // porque un https:// "puede abrirse" siempre (en el navegador) y daría un
      // falso positivo, impidiendo el fallback a la tienda.
      const { value: canOpen } = await AppLauncher.canOpenUrl({ url: platform.uriScheme });

      if (canOpen) {
        // Instalada -> abrir con el deep link (Universal Link o esquema con datos).
        await AppLauncher.openUrl({ url: deepLink });
        return;
      }

      // En Android, algunas apps (DiDi, Taxis Libres) no abren con un esquema URI
      // "vacío" aunque estén instaladas: solo responden al launch por package.
      // El formato oficial "android-app://<package>" abre la app por su paquete y
      // AppLauncher lo soporta. Verificado en dispositivo: abre DiDi correctamente.
      if (isAndroid) {
        try {
          const { value: pkgInstalled } = await AppLauncher.canOpenUrl({
            url: `android-app://${platform.androidPackage}`,
          });
          if (pkgInstalled) {
            await AppLauncher.openUrl({ url: `android-app://${platform.androidPackage}` });
            return;
          }
        } catch { /* sigue al fallback de tienda */ }
      }

      // No instalada (o no se pudo abrir) -> abrir la tienda.
      await AppLauncher.openUrl({ url: store });
      return;
    } catch {
      // Si algo falla, como último recurso intentamos abrir la tienda.
      try { await AppLauncher.openUrl({ url: store }); } catch { /* noop */ }
      return;
    }
  }

  // --- Navegador web ---
  const os = getMobileOS();
  if (platform.usesUniversalLink) {
    window.location.href = deepLink;
    return;
  }
  if (os === 'android' || os === 'ios') {
    // Intenta abrir la app por esquema; si en 1.5s la página sigue visible, va a la tienda.
    const start = Date.now();
    const timeout = setTimeout(() => {
      if (Date.now() - start < 3000) window.location.href = store;
    }, 1500);
    const handleVisibility = () => {
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.location.href = deepLink;
  } else {
    // Desktop: abrir la tienda en otra pestaña.
    window.open(store, '_blank');
  }
}

// Copia la dirección de destino al portapapeles. Se usa para las apps que NO reciben
// el destino por deep link (DiDi, Cabify, InDriver, Taxis Libres), para que el usuario
// solo tenga que pegar en lugar de escribir. Devuelve true si logró copiar.
async function copyAddressToClipboard(address: string): Promise<boolean> {
  if (!address) return false;
  try {
    if (Capacitor.isNativePlatform()) {
      await Clipboard.write({ string: address });
      return true;
    }
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(address);
      return true;
    }
  } catch { /* el navegador puede bloquear el portapapeles sin gesto del usuario */ }
  return false;
}

// Surge pricing basado en hora del día y día de la semana (multiplicadores configurables desde admin)
// Recargo nocturno desde las 8 PM (20:00). Domingos y festivos con recargo todo el día.
function getSurgeMultiplier(surcharges: PricingConfig['surcharges']): number {
  const hour = new Date().getHours();
  const day = new Date().getDay(); // 0 = domingo, 6 = sábado
  const isSunday = day === 0;
  const isFridaySaturday = day === 5 || day === 6;

  // Noche (8 PM en adelante o madrugada hasta 5 AM): recargo nocturno
  const isNight = hour >= 20 || hour <= 5;

  // Viernes/sábado noche: surge más alto por alta demanda
  if (isFridaySaturday && isNight) return surcharges.weekendNightMultiplier + Math.random() * 0.25;
  // Domingo (todo el día tiene recargo tipo festivo)
  if (isSunday) return surcharges.sundayMultiplier + Math.random() * 0.20;
  // Noche entre semana (8 PM - 5 AM)
  if (isNight) return surcharges.nightMultiplier + Math.random() * 0.18;
  // Hora pico mañana (6-9am) y tarde (5-8pm)
  if ((hour >= 6 && hour <= 9) || (hour >= 17 && hour < 20)) return surcharges.peakMultiplier + Math.random() * 0.15;
  // Hora valle: sin surge o mínimo
  return 1.0 + Math.random() * 0.05;
}

// Coordenadas aproximadas del Aeropuerto El Dorado (Bogotá)
const ELDORADO_LAT = 4.7016;
const ELDORADO_LNG = -74.1469;
const AIRPORT_RADIUS_KM = 2.5; // radio para considerar "aeropuerto"

// Distancia Haversine en km entre dos coordenadas
function distanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Determina si origen o destino está en el aeropuerto El Dorado
function involvesAirport(origin: PlaceResult, destination: PlaceResult): boolean {
  const oNear = distanceKm(origin.lat, origin.lng, ELDORADO_LAT, ELDORADO_LNG) <= AIRPORT_RADIUS_KM;
  const dNear = distanceKm(destination.lat, destination.lng, ELDORADO_LAT, ELDORADO_LNG) <= AIRPORT_RADIUS_KM;
  return oNear || dNear;
}

// Estructura de configuración de tarifas (editable desde el panel admin)
interface PlatformRate {
  base: number;
  perKm: number;
  perMin: number;
  minFare: number;
}
interface PricingConfig {
  platforms: Record<string, PlatformRate>;
  surcharges: {
    airport: number;
    nightMultiplier: number;
    sundayMultiplier: number;
    peakMultiplier: number;
    weekendNightMultiplier: number;
  };
}

// Tarifas por defecto (fallback si el backend no responde). Calibradas Bogotá 2026.
const DEFAULT_PRICING: PricingConfig = {
  platforms: {
    uber:     { base: 4500, perKm: 900, perMin: 170, minFare: 9000 },
    didi:     { base: 3200, perKm: 600, perMin: 105, minFare: 7000 },
    indriver: { base: 3000, perKm: 560, perMin: 110, minFare: 6000 },
    // Cabify calibrado con tarifas oficiales publicadas (Bogotá). Fuente: cabify.com/co/tarifas
    cabify:   { base: 3000, perKm: 595, perMin: 180, minFare: 3500 },
    yango:    { base: 3500, perKm: 680, perMin: 120, minFare: 7500 },
    // Taxis Libres: taxi con taxímetro oficial regulado (Bogotá). Aproximación por
    // unidades convertidas a $/km y $/min, con banderazo (base) y mínima regulada.
    taxislibres: { base: 3200, perKm: 1200, perMin: 110, minFare: 6500 },
  },
  surcharges: {
    airport: 8000,
    nightMultiplier: 1.20,
    sundayMultiplier: 1.20,
    peakMultiplier: 1.12,
    weekendNightMultiplier: 1.30,
  },
};

const HISTORY_KEY = 'fuigo-search-history';

function loadHistory(): HistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch { return []; }
}

function saveHistory(entries: HistoryEntry[]) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, 20)));
}

export default function Home() {
  const [origin, setOrigin] = useState<PlaceResult | null>(null);
  const [destination, setDestination] = useState<PlaceResult | null>(null);
  const [originText, setOriginText] = useState('');
  const [destText, setDestText] = useState('');
  const [routeInfo, setRouteInfo] = useState<{ distance: string; duration: string } | null>(null);
  const [estimates, setEstimates] = useState<PlatformEstimate[]>([]);
  const [loading, setLoading] = useState(false);
  const [locatingUser, setLocatingUser] = useState(false);
  const [mapsReady, setMapsReady] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistory());
  const [feedbackModal, setFeedbackModal] = useState<{ show: boolean; platform: string }>({ show: false, platform: '' });
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [pricing, setPricing] = useState<PricingConfig>(DEFAULT_PRICING);
  // Aviso temporal cuando se copia el destino al portapapeles al abrir una app.
  const [copyToast, setCopyToast] = useState('');

  // Cargar tarifas configurables desde el backend (con fallback a las por defecto)
  useEffect(() => {
    fetch(`${API_BASE_URL}/admin/pricing`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.pricing?.platforms && data?.pricing?.surcharges) {
          setPricing(data.pricing);
        }
      })
      .catch(() => { /* usa DEFAULT_PRICING */ });
  }, []);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const directionsRenderer = useRef<any>(null);
  const originInputRef = useRef<HTMLInputElement>(null);
  const destInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuthStore();

  // Submit feedback to server
  const submitFeedback = async () => {
    if (!feedbackText.trim()) return;
    setFeedbackSending(true);
    try {
      const authData = JSON.parse(localStorage.getItem('fuigo-auth') || '{}');
      const token = authData?.state?.token;
      if (token) {
        await fetch(`${API_BASE_URL}/admin/feedback`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ comment: feedbackText, rating: feedbackRating, platform: feedbackModal.platform }),
        });
      }
    } catch { /* ignore */ }
    setFeedbackSending(false);
    setFeedbackModal({ show: false, platform: '' });
    setFeedbackText('');
    setFeedbackRating(0);
  };

  // Handle platform click: track + show feedback modal
  const handlePlatformClick = async (platformId: string, deepLink: string, platformConfig: PlatformConfig) => {
    trackPlatformClick(platformId, user?.userId || '', user?.name || '');

    const destAddress = destination?.address || destText || '';

    // Para apps que no reciben el destino por deep link (DiDi, Cabify, InDriver,
    // Taxis Libres), copiamos la dirección de destino al portapapeles para que el
    // usuario solo tenga que pegarla (algunas apps como Taxis Libres incluso la
    // toman automáticamente).
    if (platformConfig.copiesAddress && destAddress) {
      setCopyToast(`📋 Copiamos tu destino. Pégalo en ${platformConfig.name}.`);
      setTimeout(() => setCopyToast(''), 5000);
      // Copiamos y esperamos un instante para asegurar que el portapapeles quede
      // escrito antes de que la app externa pase a primer plano.
      await copyAddressToClipboard(destAddress);
      await new Promise((r) => setTimeout(r, 150));
    }

    if (platformConfig.usesUniversalLink && !Capacitor.isNativePlatform()) {
      window.location.href = deepLink;
    } else {
      openNativeApp(platformConfig, deepLink);
    }
    // Show feedback modal after a short delay
    setTimeout(() => setFeedbackModal({ show: true, platform: platformId }), 2000);
  };

  // Initialize map
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || !window.google) return;
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 4.711, lng: -74.0721 },
        zoom: 12,
        styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
        mapTypeControl: false,
        streetViewControl: false,
      });
      directionsRenderer.current = new window.google.maps.DirectionsRenderer({
        map: mapInstance.current,
        suppressMarkers: false,
        polylineOptions: { strokeColor: '#2563EB', strokeWeight: 5 },
      });
      setMapsReady(true);
    };
    if (window.google?.maps) { initMap(); }
    else {
      const check = setInterval(() => {
        if (window.google?.maps) { clearInterval(check); initMap(); }
      }, 200);
      return () => clearInterval(check);
    }
  }, []);

  // Setup autocomplete
  useEffect(() => {
    if (!mapsReady || !window.google) return;
    const options = { componentRestrictions: { country: 'co' }, fields: ['formatted_address', 'geometry', 'place_id'] };
    if (originInputRef.current) {
      const ac = new window.google.maps.places.Autocomplete(originInputRef.current, options);
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place.geometry) {
          setOrigin({ address: place.formatted_address, lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), placeId: place.place_id });
          setOriginText(place.formatted_address);
        }
      });
    }
    if (destInputRef.current) {
      const ac = new window.google.maps.places.Autocomplete(destInputRef.current, options);
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place.geometry) {
          setDestination({ address: place.formatted_address, lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), placeId: place.place_id });
          setDestText(place.formatted_address);
        }
      });
    }
  }, [mapsReady]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocatingUser(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        if (window.google) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
            setLocatingUser(false);
            if (status === 'OK' && results[0]) {
              const addr = results[0].formatted_address;
              setOrigin({ address: addr, lat, lng, placeId: results[0].place_id });
              setOriginText(addr);
              if (originInputRef.current) originInputRef.current.value = addr;
            }
          });
        } else {
          setLocatingUser(false);
          setOrigin({ address: `${lat}, ${lng}`, lat, lng, placeId: '' });
          setOriginText(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
        }
      },
      () => { setLocatingUser(false); alert('No se pudo obtener tu ubicación. Verifica los permisos.'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Calculate route and estimates
  const handleSearch = useCallback(async () => {
    if (!origin || !destination || !window.google) return;
    setLoading(true);
    setEstimates([]);

    try {
      const directionsService = new window.google.maps.DirectionsService();
      const result = await directionsService.route({
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      });
      if (directionsRenderer.current) directionsRenderer.current.setDirections(result);

      const leg = result.routes[0].legs[0];
      const distKm = leg.distance.value / 1000;
      const durMin = Math.ceil(leg.duration.value / 60);
      setRouteInfo({ distance: leg.distance.text, duration: leg.duration.text });

      const surge = getSurgeMultiplier(pricing.surcharges);
      const hasAirport = involvesAirport(origin, destination);
      const airportSurcharge = pricing.surcharges.airport;

      // Variación aleatoria pequeña por plataforma para simular fluctuación natural
      const varianceByPlatform: Record<string, number> = {
        uber: 0.02, didi: 0.03, indriver: 0.05, cabify: 0.02, yango: 0.02,
      };

      const platformEstimates: PlatformEstimate[] = PLATFORMS.map((p) => {
        // Tarifas leídas del backend (configurables desde el panel admin)
        const pr = pricing.platforms[p.id] || DEFAULT_PRICING.platforms[p.id];
        const platformSurge = p.id === 'indriver' ? 1.0 : surge; // InDriver no tiene tarifa dinámica
        const variance = 1 + (Math.random() * 2 - 1) * (varianceByPlatform[p.id] ?? 0.03);
        // Recargo de aeropuerto (El Dorado). InDriver aplica un recargo menor por ser negociable.
        const airportFee = hasAirport ? (p.id === 'indriver' ? airportSurcharge * 0.4 : airportSurcharge) : 0;
        const rawPrice = Math.max(
          (pr.base + pr.perKm * distKm + pr.perMin * durMin) * platformSurge * variance + airportFee,
          pr.minFare
        );
        const price = roundTo100(rawPrice);

        // Nivel de confianza y rango de precio.
        // Cabify (oficial) => rango cero, precio puntual.
        // Estimadas/negociable => rango ±spread alrededor del precio, con piso en la tarifa mínima.
        const confidence = CONFIDENCE[p.id] || DEFAULT_CONFIDENCE;
        const priceMin = confidence.rangeSpread > 0
          ? Math.max(roundTo100(price * (1 - confidence.rangeSpread)), pr.minFare)
          : price;
        const priceMax = confidence.rangeSpread > 0
          ? roundTo100(price * (1 + confidence.rangeSpread))
          : price;

        return {
          platform: p.name,
          id: p.id,
          price,
          estimatedTime: durMin + Math.floor(Math.random() * 4),
          vehicleType: 'Estándar',
          logo: p.logo,
          deepLink: p.buildDeepLink(origin, destination),
          storeLink: getMobileOS() === 'ios' ? p.iosStore : p.androidStore,
          surgeMultiplier: platformSurge,
          platformConfig: p,
          priceMin,
          priceMax,
          confidence,
        };
      });

      platformEstimates.sort((a, b) => a.price - b.price);
      setEstimates(platformEstimates);

      // Save to history
      const entry: HistoryEntry = {
        id: Date.now().toString(),
        date: new Date().toLocaleString('es-CO'),
        origin, destination,
        originText: originText || origin.address,
        destText: destText || destination.address,
        cheapest: platformEstimates[0].platform,
        cheapestPrice: platformEstimates[0].price,
      };
      const updated = [entry, ...history.filter(h => h.id !== entry.id)].slice(0, 20);
      setHistory(updated);
      saveHistory(updated);
    } catch (err) {
      console.error('Error calculating route:', err);
    } finally {
      setLoading(false);
    }
  }, [origin, destination, originText, destText, history, pricing]);

  // Auto-search when both places are selected
  useEffect(() => {
    if (origin && destination) handleSearch();
  }, [origin, destination]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load a history entry
  const loadFromHistory = (entry: HistoryEntry) => {
    setOrigin(entry.origin);
    setDestination(entry.destination);
    setOriginText(entry.originText);
    setDestText(entry.destText);
    if (originInputRef.current) originInputRef.current.value = entry.originText;
    if (destInputRef.current) destInputRef.current.value = entry.destText;
    setShowHistory(false);
  };

  return (
    <div className="space-y-6">
      {/* Aviso flotante cuando se copia el destino al portapapeles */}
      {copyToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg max-w-[90%] text-center">
          {copyToast}
        </div>
      )}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">
          {user?.isGuest ? '¡Bienvenido a Fuigo!' : `¡Hola, ${user?.name?.split(' ')[0]}!`}
        </h1>
        <p className="mt-1 text-gray-600">¿A dónde quieres ir?</p>
        {user?.isGuest && (
          <p className="mt-1 text-sm text-blue-600">
            <a href="/register" className="underline">Crea una cuenta</a> para guardar tu historial de rutas
          </p>
        )}
      </div>

      {/* Search inputs */}
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-md p-6 space-y-4">
        <div className="relative">
          <span className="absolute left-3 top-3.5 text-green-500 text-lg">●</span>
          <input
            ref={originInputRef}
            type="text"
            value={originText}
            onChange={(e) => setOriginText(e.target.value)}
            className="w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="¿Dónde estás? (Origen)"
          />
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={locatingUser}
            className="absolute right-2 top-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 disabled:opacity-50"
          >
            {locatingUser ? '📍 Ubicando...' : '📍 Mi ubicación'}
          </button>
        </div>
        <div className="relative">
          <span className="absolute left-3 top-3.5 text-red-500 text-lg">●</span>
          <input
            ref={destInputRef}
            type="text"
            value={destText}
            onChange={(e) => setDestText(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="¿A dónde vas? (Destino)"
          />
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            🕐 {showHistory ? 'Ocultar historial' : 'Ver historial de rutas'}
          </button>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              Calculando...
            </div>
          )}
        </div>
      </div>

      {/* History panel */}
      {showHistory && (
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-md p-4">
          <h3 className="font-bold text-gray-900 mb-3">Historial de rutas</h3>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500">No hay búsquedas recientes</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {history.map((h) => (
                <button
                  key={h.id}
                  onClick={() => loadFromHistory(h)}
                  className="w-full text-left p-3 rounded-lg hover:bg-gray-50 border border-gray-100 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        <span className="text-green-500">●</span> {h.originText}
                      </p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        <span className="text-red-500">●</span> {h.destText}
                      </p>
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <p className="text-sm font-bold text-blue-600">${h.cheapestPrice.toLocaleString()}</p>
                      <p className="text-xs text-gray-400">{h.cheapest}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{h.date}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Map */}
      <div className="max-w-4xl mx-auto">
        <div ref={mapRef} className="w-full h-80 rounded-xl shadow-md overflow-hidden bg-gray-200" />
      </div>

      {/* Route info + surge warning */}
      {routeInfo && (
        <div className="max-w-2xl mx-auto space-y-3">
          <div className="flex justify-center gap-8 text-center">
            <div className="bg-white px-6 py-3 rounded-lg shadow">
              <p className="text-sm text-gray-500">Distancia</p>
              <p className="text-lg font-bold text-gray-900">{routeInfo.distance}</p>
            </div>
            <div className="bg-white px-6 py-3 rounded-lg shadow">
              <p className="text-sm text-gray-500">Tiempo estimado</p>
              <p className="text-lg font-bold text-gray-900">{routeInfo.duration}</p>
            </div>
          </div>
          {estimates.some(e => e.surgeMultiplier > 1.15) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
              <p className="text-sm text-yellow-800">
                ⚡ Tarifa dinámica activa — los precios pueden ser más altos de lo normal por alta demanda
              </p>
            </div>
          )}
        </div>
      )}

      {/* Platform estimates */}
      {estimates.length > 0 && (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">Precios estimados</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {estimates.map((est, i) => (
              <div
                key={est.id}
                className={`bg-white rounded-xl shadow-md overflow-hidden border-2 ${i === 0 ? 'border-green-500' : 'border-transparent'} hover:shadow-lg transition-shadow`}
              >
                {i === 0 && (
                  <div className="bg-green-500 text-white text-center text-xs font-bold py-1">
                    💰 MEJOR PRECIO
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    {/* Distintivo de color con la inicial: se ve igual en Android e iOS
                        (los emojis se renderizaban distinto en cada sistema). */}
                    <span
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold ${est.platformConfig.colorClass}`}
                      aria-hidden="true"
                    >
                      {est.platform.charAt(0)}
                    </span>
                    <span className="text-lg font-bold">{est.platform}</span>
                    {est.surgeMultiplier > 1.15 && (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">
                        ⚡ x{est.surgeMultiplier.toFixed(1)}
                      </span>
                    )}
                  </div>
                  {/* Insignia de nivel de confianza de la estimación */}
                  <span
                    className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full mb-2 ${est.confidence.badgeClass}`}
                    title={est.confidence.tooltip}
                  >
                    {est.confidence.level === 'oficial' ? '✓ ' : est.confidence.level === 'negociable' ? '🤝 ' : '≈ '}
                    {est.confidence.label}
                  </span>
                  {/* Precio: puntual si es oficial, rango si es estimado, "desde" si es negociable */}
                  {est.confidence.level === 'oficial' ? (
                    <p className="text-3xl font-bold text-blue-600 mb-1">
                      ${est.price.toLocaleString()}
                    </p>
                  ) : est.confidence.level === 'negociable' ? (
                    <p className="text-2xl font-bold text-blue-600 mb-1">
                      desde ${est.priceMin.toLocaleString()}
                    </p>
                  ) : (
                    <p className="text-xl font-bold text-blue-600 mb-1 leading-tight">
                      ${est.priceMin.toLocaleString()} – ${est.priceMax.toLocaleString()}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mb-3">COP • {est.vehicleType}</p>
                  <p className="text-sm text-gray-600 mb-4">⏱ {est.estimatedTime} min</p>

                  {/* Open app */}
                  <button
                    type="button"
                    onClick={() => handlePlatformClick(est.id, est.deepLink, est.platformConfig)}
                    className={`block w-full py-2.5 px-4 ${
                      est.id === 'uber' ? 'bg-black' :
                      est.id === 'didi' ? 'bg-orange-500' :
                      est.id === 'indriver' ? 'bg-green-600' :
                      est.id === 'cabify' ? 'bg-purple-600' :
                      est.id === 'yango' ? 'bg-red-500' :
                      est.id === 'taxislibres' ? 'bg-yellow-500' : 'bg-gray-700'
                    } text-white text-center rounded-lg font-medium hover:opacity-90 transition-opacity`}
                  >
                    Abrir {est.platform}
                  </button>
                  {/* Aviso permanente: estas apps no reciben el destino por enlace,
                      así que copiamos la dirección para que el usuario la pegue. */}
                  {est.platformConfig.copiesAddress && (
                    <p className="flex items-center justify-center gap-1 text-center text-[11px] text-gray-500 mt-2">
                      📋 Copiaremos tu destino para que lo pegues en el app
                    </p>
                  )}
                  <a
                    href={est.storeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center text-xs text-gray-400 mt-2 hover:text-blue-500"
                  >
                    ¿No tienes la app? Descargar
                  </a>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-500 mt-4 max-w-2xl mx-auto">
            Los precios mostrados son estimaciones referenciales basadas en las tarifas públicas de cada plataforma en Colombia. 
            El precio final puede variar según demanda, tráfico y condiciones del momento. 
            En <span className="font-semibold">Uber</span> y <span className="font-semibold">Yango</span> se abre 
            la ruta directamente con las coordenadas de tu viaje. En <span className="font-semibold">DiDi</span>, <span className="font-semibold">Cabify</span>, <span className="font-semibold">InDriver</span> y <span className="font-semibold">Taxis Libres</span> copiamos
            tu dirección de destino para que la pegues al abrir la app.
          </p>
        </div>
      )}

      {/* Feedback Modal */}
      {feedbackModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              💬 ¿Cómo fue tu experiencia con <span className="capitalize">{feedbackModal.platform}</span>?
            </h3>
            <p className="text-sm text-gray-500 mb-4">Tu opinión nos ayuda a mejorar Fuigo</p>
            
            <div className="flex gap-1 mb-3">
              {[1, 2, 3, 4, 5].map(star => (
                <button key={star} type="button" onClick={() => setFeedbackRating(star)}
                  className={`text-2xl ${star <= feedbackRating ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}>
                  ⭐
                </button>
              ))}
            </div>
            
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Escribe tu comentario aquí..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              rows={3}
            />
            
            <div className="flex gap-3 mt-4">
              <button type="button" onClick={() => { setFeedbackModal({ show: false, platform: '' }); setFeedbackText(''); setFeedbackRating(0); }}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
                Omitir
              </button>
              <button type="button" onClick={submitFeedback} disabled={feedbackSending || !feedbackText.trim()}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {feedbackSending ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
